import { v4 as uuidv4 } from "uuid";
import { TABLET_WIDTH } from "@circle-react-shared/BuilderUI/Canvas/constants";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import communityImagePlaceholder from "@circle-react-shared/BuilderUI/DefaultBlocks/CallToAction/CardCallToAction/community_image_placeholder.png";
import heartImagePlaceholder from "@circle-react-shared/BuilderUI/DefaultBlocks/CallToAction/CardCallToAction/heart_image_placeholder.png";
import supportImagePlaceholder from "@circle-react-shared/BuilderUI/DefaultBlocks/CallToAction/CardCallToAction/support_image_placeholder.png";
import trophyImagePlaceholder from "@circle-react-shared/BuilderUI/DefaultBlocks/CallToAction/CardCallToAction/trophy_image_placeholder.png";
import userImagePlaceholder from "@circle-react-shared/BuilderUI/DefaultBlocks/CallToAction/CardCallToAction/user_image_placeholder.png";
import usersImagePlaceholder from "@circle-react-shared/BuilderUI/DefaultBlocks/CallToAction/CardCallToAction/users_image_placeholder.png";
import { createCardBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/layoutBlockHelpers";
import {
  createH2Block,
  createPBlock,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textBlocksHelpers";
import {
  DesignProp,
  GeneralProp,
  MarginsProp,
  PaddingsProp,
} from "@circle-react-shared/BuilderUI/constants/PropCategories";
import {
  getMarginOptions,
  getPaddingOptions,
} from "@circle-react-shared/BuilderUI/utils/boxModelStyleHelpers";
import { getBackgroundColorOptions } from "@circle-react-shared/BuilderUI/utils/buttonStyleHelpers";
import { getMaxWidthOptions } from "@circle-react-shared/BuilderUI/utils/sizeStyleHelpers";
import { getTextAlignOptions } from "@circle-react-shared/BuilderUI/utils/textStyleHelpers";
import { primaryBlockDefaultStyles } from "../constants";

const cards = [
  {
    image: {
      signedId: "",
      placeholder: userImagePlaceholder,
    },
    h5Text: "Personalized Coaching Approach",
    pText:
      "Unlike one-size-fits-all programs, we create a custom roadmap based on your unique strengths, challenges, and career aspirations. No generic advice—just strategies that work for Your situation.",
  },
  {
    image: {
      signedId: "",
      placeholder: communityImagePlaceholder,
    },
    h5Text: "Proven Leadership Methodology",
    pText:
      "Our framework has helped executives at Google, Microsoft, and Amazon achieve breakthroughs, refined through 10,000+ coaching hours across 30+ industries.",
  },
  {
    image: {
      signedId: "",
      placeholder: usersImagePlaceholder,
    },
    h5Text: "Exclusive Leadership Community",
    pText:
      "Most leadership journeys feel lonely—but not here. Join a community that celebrates wins, overcomes challenges, and builds connections that become career-defining relationships and opportunities.",
  },
  {
    image: {
      signedId: "",
      placeholder: trophyImagePlaceholder,
    },
    h5Text: "Real-World Success Stories",
    pText:
      "Our clients don't just get better at leadership—they drive real, lasting impact. We measure success by your results, not just how good the coaching feels in the moment and beyond.",
  },
  {
    image: {
      signedId: "",
      placeholder: supportImagePlaceholder,
    },
    h5Text: "Ongoing Support and Accountability",
    pText:
      "Most leadership programs leave you on your own. We don’t. Get ongoing coaching, an engaged community, weekly mastermind calls, and real-time support exactly when you need it most.",
  },
  {
    image: {
      signedId: "",
      placeholder: heartImagePlaceholder,
    },
    h5Text: "Confidential Partnership",
    pText:
      "Leadership challenges require a safe space. Everything shared in our coaching sessions and community remains 100% confidential, allowing you to be vulnerable, authentic, and open about your real challenges.",
  },
] as const;

export const defaultData: BaseBlock = {
  id: uuidv4(),
  type: "HomepageBlock4",
  meta: {},
  props: {
    header: {
      meta: {
        name: "Header",
        nameI18n: "builder_ui.prop_type.header",
      },
      prop: "children",
      type: "children",
      value: [
        createH2Block({
          text: "Coaching + Community = Unstoppable Growth",
          styles: {
            textAlign: getTextAlignOptions("center"),
            maxWidth: getMaxWidthOptions({
              defaultValue: `${TABLET_WIDTH}px`,
            }),
          },
        }),
        createPBlock({
          text: "At Clarity Coaching, we combine expert coaching with a powerful network of leaders. You get tailored support, peer-driven insights, and an ongoing learning environment that accelerates your success.",
          styles: {
            textAlign: getTextAlignOptions("center"),
            maxWidth: getMaxWidthOptions({
              defaultValue: `${TABLET_WIDTH}px`,
            }),
          },
        }),
      ],
    },
    content: {
      meta: {
        name: "Content",
        nameI18n: "builder_ui.prop_type.content",
      },
      prop: "children",
      type: "children",
      value: cards.map(card => createCardBlock(card)),
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        backgroundColor: getBackgroundColorOptions({
          defaultValue: primaryBlockDefaultStyles.backgroundColor,
          category: [DesignProp, GeneralProp],
        }),
        padding: getPaddingOptions({
          defaultValue: primaryBlockDefaultStyles.padding,
          category: [DesignProp, PaddingsProp],
        }),
        margin: getMarginOptions({
          defaultValue: primaryBlockDefaultStyles.margin,
          category: [DesignProp, MarginsProp],
        }),
      },
      meta: {
        name: "Styles",
        nameI18n: "builder_ui.prop_type.styles",
        category: [],
      },
    },
  },
};
